import { useEffect } from "react";
import { Group, Line, Circle, Text } from "react-konva";
import { moduleGroups, rowGaps, sectionsOf, sectionSpan } from "@/lib/reportSection";
import { placeLabels } from "@/lib/labels";

import { tr } from "@/lib/i18n";
const f2 = (v) => v.toFixed(2).replace(".", ",");

const Lbl = ({ x, y, text, scale, color, size }) => (
  <Text x={x} y={y} text={text} fontSize={size / scale} fontStyle="bold" fill={color} stroke="#0f172a" strokeWidth={2.6 / scale}
    fillAfterStrokeEnabled offsetX={(text.length * size * 0.31) / scale} offsetY={(size * 0.5) / scale} listening={false} />
);

const Dim = ({ a, b, scale, color = "#22d3ee" }) => (
  <Line points={[...a, ...b]} stroke={color} strokeWidth={1.4 / scale} listening={false} />
);

// quote d'ingombro dei gruppi di moduli (file sui tetti piani, con spazio tra le file) e linee di sezione A–A trascinabili
export const PlanMarks = ({ layout, flat, scale, llToWorld, showGroups, canDrag, onSectionStart, onSectionMove, fixedBoxes = [], onHidden }) => {
  const W = (f, u, v) => llToWorld(f.frame.toLL([u, v]));
  const mid = (p, q) => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
  const secs = flat ? [] : sectionsOf(layout);
  // livelli: 1 lunghezze di gruppi/file (sempre), 2 righe × colonne e spazi tra le file, 3 tutte le quote dei gruppi piccoli (≤ 2 moduli)
  const labels = [];
  const add = (at, text, dir, level, dim, color = "#a5f3fc", size = 11) => labels.push({
    x: at[0] * scale, y: at[1] * scale, w: text.length * size * 0.62 + 4, h: size * 1.25, text, color, size, dir, level, dim,
    lineLen: dim ? Math.hypot(dim.b[0] - dim.a[0], dim.b[1] - dim.a[1]) * scale : undefined,
  });
  if (showGroups) for (const f of layout.fields) {
    for (const g of moduleGroups(f, flat)) {
      const small = g.n <= 2, o = flat ? 0.15 : 0.45, a = W(f, g.u0, g.v0 - o), b = W(f, g.u1, g.v0 - o), dir = [b[0] - a[0], b[1] - a[1]];
      if (g.flatRow) { add(mid(a, b), tr("{0} m · {1} mod.", [f2(g.width), g.n]), dir, small ? 3 : 1, { a, b }); continue; }
      const c = W(f, g.u0 - o, g.v0), d = W(f, g.u0 - o, g.v1);
      add(mid(a, b), `${f2(g.width)} m`, dir, small ? 3 : 1, { a, b });
      add(mid(c, d), `${f2(g.length)} m`, [d[0] - c[0], d[1] - c[1]], small ? 3 : 1, { a: c, b: d });
      add(W(f, (g.u0 + g.u1) / 2, (g.v0 + g.v1) / 2), `${g.rows} × ${g.cols}${g.full ? "" : "*"}`, dir, small ? 3 : 2, null, "#ffffff", 13);
    }
    if (flat) for (const gp of rowGaps(f)) {
      const a = W(f, gp.u, gp.va), b = W(f, gp.u, gp.vb);
      add(mid(a, b), f2(gp.gap), [b[0] - a[0], b[1] - a[1]], 2, { a, b, color: "#f472b6" }, "#fbcfe8");
    }
  }
  const secLines = secs.map((sc) => {
    const span = sectionSpan(sc.f, sc.u);
    return { sc, A: W(sc.f, sc.u, span[0] - 0.6), B: W(sc.f, sc.u, span[1] + 0.6) };
  });
  const fixed = [...fixedBoxes, ...secLines.flatMap(({ A, B }) => [A, B].map((q) => ({ x: q[0] * scale, y: q[1] * scale, w: 20, h: 20 })))];
  const all = placeLabels(labels, fixed), placed = all.filter((l) => !l.hidden), dims = placed.map((l) => l.dim).filter(Boolean);
  const nHidden = all.length - placed.length;
  useEffect(() => { onHidden?.(nHidden); }, [nHidden]); // eslint-disable-line
  return (
    <Group>
      {dims.map((d, i) => <Dim key={`d${i}`} a={d.a} b={d.b} scale={scale} color={d.color} />)}
      {placed.map((l, i) => (
        <Group key={`l${i}`} data-testid="editor-group-dim" listening={false}>
          {l.leader && <Line points={[l.ax / scale, l.ay / scale, l.x / scale, l.y / scale]} stroke="rgba(255,255,255,0.85)" strokeWidth={1 / scale} />}
          <Lbl x={l.x / scale} y={l.y / scale} text={l.text} scale={scale} color={l.color} size={l.size} />
        </Group>
      ))}
      {secLines.map(({ sc, A, B }) => (
        <Group key={`sec-${sc.f.roof.id}`}>
          <Line name={`section-${sc.f.roof.id}`} points={[...A, ...B]} stroke="#ef4444" strokeWidth={2.4 / scale} hitStrokeWidth={14 / scale}
            dash={[10 / scale, 4 / scale, 3 / scale, 4 / scale]} draggable={canDrag}
            onDragStart={onSectionStart} onDragMove={(e) => { const st = e.target.getStage().getRelativePointerPosition(); e.target.position({ x: 0, y: 0 }); onSectionMove(sc.f, st); }}
            onMouseEnter={(e) => { if (canDrag) e.target.getStage().container().style.cursor = "ew-resize"; }}
            onMouseLeave={(e) => { e.target.getStage().container().style.cursor = ""; }} />
          {[A, B].map((q, i) => (
            <Group key={i} listening={false}>
              <Circle x={q[0]} y={q[1]} radius={9 / scale} fill="#ef4444" />
              <Lbl x={q[0]} y={q[1]} text={sc.letter} scale={scale} color="#ffffff" size={12} />
            </Group>
          ))}
        </Group>
      ))}
    </Group>
  );
};
